import React, { useState } from "react";
import { insertComment } from "../../../api/commentAPI";
import Swal from "sweetalert2";
import { Tooltip, Whisper } from "rsuite";

const CommentSection = ({ blog, setBlog }) => {
  const [newComment, setNewComment] = useState("");
  const [newName, setNewName] = useState("");
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const [showCommentTooltip, setShowCommentTooltip] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const nameTooltip = <Tooltip>Maximum 50 characters allowed</Tooltip>;
  const commentTooltip = <Tooltip>Maximum 150 characters allowed</Tooltip>;

  const handleNameChange = (e) => {
    const value = e.target.value;
    setNewName(value);
    setShowNameTooltip(value.length >= 50);
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    setNewComment(value);
    setShowCommentTooltip(value.length >= 150);
  };

  const handleInsertComment = async () => {
    if (newComment.trim() === "" || newName.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Comment and Name cannot be empty",
      });
      return;
    }
    if (newName.length > 50) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Name cannot exceed 50 characters",
      });
      return;
    }
    if (newComment.length > 150) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Comment cannot exceed 150 characters",
      });
      return;
    }

    try {
      setSubmitting(true);
      const commentData = {
        comment: newComment,
        blog_id: blog.id,
        name: newName,
      };
      const insertedComment = await insertComment(commentData);

      if (!insertedComment.created_at) {
        insertedComment.created_at = new Date().toISOString();
      }

      setBlog((prevBlog) => {
        if (prevBlog) {
          return {
            ...prevBlog,
            comments: [...prevBlog.comments, insertedComment],
          };
        }
        return prevBlog;
      });
      setNewComment("");
      setNewName("");
      window.location.reload();
    } catch (error) {
      console.error("Failed to insert comment:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to add comment",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (dt) =>
    new Date(dt).toLocaleString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="text-slate-800">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-1.5 h-7 rounded-full bg-gradient-to-b from-brand-500 to-eco-500" />
        <h2 className="font-oswald text-2xl font-semibold text-slate-900">
          Diskusi ({blog.comments.length})
        </h2>
      </div>

      {/* Comments list */}
      {blog.comments.length > 0 ? (
        <div className="space-y-4 mb-8">
          {blog.comments.map((comment) => (
            <div
              key={comment.id}
              className="group p-5 bg-slate-50 hover:bg-white border border-slate-100 hover:border-brand-200 rounded-2xl transition-all"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-500 to-eco-500 flex items-center justify-center text-white text-base font-bold shadow-md">
                  {comment.name?.charAt(0).toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="font-oswald text-base font-semibold text-slate-900 leading-tight">
                    {comment.name}
                  </h5>
                  <p className="text-xs text-slate-500 font-overpass">
                    {formatDateTime(comment.created_at)}
                  </p>
                </div>
              </div>
              <p className="font-overpass text-slate-700 text-sm lg:text-base leading-relaxed break-words pl-14">
                {comment.comment}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-10 mb-8 rounded-2xl bg-slate-50 border border-dashed border-slate-200">
          <div className="text-3xl mb-2">💬</div>
          <p className="text-sm text-slate-500 font-overpass">
            Belum ada komentar. Jadilah yang pertama berkomentar!
          </p>
        </div>
      )}

      {/* Add Comment */}
      <div className="rounded-2xl bg-gradient-to-br from-brand-50/50 via-white to-eco-50/50 border border-slate-100 p-6">
        <h3 className="font-oswald text-lg font-semibold text-slate-900 mb-4">
          Tambahkan Komentar
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5 font-overpass">
              Nama Anda
            </label>
            <Whisper
              trigger="active"
              placement="top"
              speaker={nameTooltip}
              open={showNameTooltip}
            >
              <input
                type="text"
                className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass transition-all placeholder:text-slate-400"
                placeholder="Masukkan nama..."
                value={newName}
                maxLength={50}
                onChange={handleNameChange}
              />
            </Whisper>
            <div className="text-xs text-slate-400 mt-1 font-overpass text-right">
              {newName.length}/50
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5 font-overpass">
              Komentar
            </label>
            <Whisper
              trigger="active"
              placement="top"
              speaker={commentTooltip}
              open={showCommentTooltip}
            >
              <textarea
                className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass transition-all min-h-[100px] resize-none placeholder:text-slate-400"
                placeholder="Tulis komentar Anda..."
                value={newComment}
                maxLength={150}
                onChange={handleCommentChange}
              />
            </Whisper>
            <div className="text-xs text-slate-400 mt-1 font-overpass text-right">
              {newComment.length}/150
            </div>
          </div>

          <button
            onClick={handleInsertComment}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-brand-600 to-eco-600 text-white font-semibold text-sm shadow-lg shadow-brand-500/30 hover:shadow-brand-500/50 transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            {submitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Mengirim...
              </>
            ) : (
              <>
                Kirim Komentar
                <span>→</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommentSection;
